import React from "react";
import { Badge } from "@/components/ui/badge";
import { formatEntityTypeForDisplay } from "@/lib/utils/crm/linked-entity";
import type { SimpleFieldConfig, FieldValue } from "../types/index";
import {
  renderTicketCell,
  renderUserCell,
  renderContactCell,
  renderCompanyCell,
  renderDealCell,
} from "./cell-rendering-relations";
import { renderScalarCell } from "./cell-rendering-scalars";
import { renderContactMethodCell } from "./cell-rendering-contact-methods";

export { engagementStalenessBadgeClassNames } from "./cell-value-rendering-shared";

export type RenderCellValueOptions = {
  /**
   * Entity cards: skip contact deep-links and stopPropagation so the card can receive
   * the click and open the detail modal.
   */
  cardSurface?: boolean;
  /** Row/detail record used to prefill compose/SMS modals when a contact method is clicked. */
  entityRecord?: Record<string, unknown>;
  /** Entity slug (e.g. contacts, companies) for modal payloads. */
  entityType?: string;
};

/**
 * Renders a cell value based on field configuration and value type.
 * This is a read-only display function used across tables, modals, and detail views.
 */
export const renderCellValue = (
  value: FieldValue,
  fieldConfig: SimpleFieldConfig,
  fieldId?: string,
  isEditing: boolean = false,
  options?: RenderCellValueOptions
): React.ReactNode => {
  const cardSurface = options?.cardSurface ?? false;
  const entityContext =
    options?.entityRecord || options?.entityType
      ? {
          item: options.entityRecord,
          entityType: options.entityType,
        }
      : undefined;
  const engagementStaleness = Boolean(fieldConfig?.engagementStaleness);

  if ((value === null || value === undefined) && !engagementStaleness) return "-";

  if (!fieldConfig) return String(value);

  const id = fieldId ?? "";

  // entity_type badge
  if (id === "entity_type") {
    const raw = String(value).trim();
    if (!raw) return "-";
    return (
      <Badge variant="outline" className="font-normal text-xs">
        {formatEntityTypeForDisplay(raw)}
      </Badge>
    );
  }

  // Probability: colored percentage
  if (
    id === "close_probability" &&
    (fieldConfig.type === "number" || fieldConfig.validation?.max === 100)
  ) {
    const num = Math.min(100, Math.max(0, Math.round(Number(value) || 0)));
    const r = (a: number, b: number, t: number) => Math.round(a + (b - a) * t);
    const color =
      num <= 50
        ? `rgb(${r(239, 234, num / 50)} ${r(68, 179, num / 50)} ${r(68, 8, num / 50)})`
        : `rgb(${r(234, 34, (num - 50) / 50)} ${r(179, 197, (num - 50) / 50)} ${r(8, 94, (num - 50) / 50)})`;
    return (
      <span className="tabular-nums font-medium" style={{ color }}>
        {num}%
      </span>
    );
  }

  // Relation fields
  const ticketResult = renderTicketCell(value, id, isEditing);
  if (ticketResult !== null) return ticketResult;

  const userResult = renderUserCell(value, id, isEditing);
  if (userResult !== null) return userResult;

  const contactResult = renderContactCell(value, id, isEditing, cardSurface);
  if (contactResult !== null) return contactResult;

  const companyResult = renderCompanyCell(value, id, isEditing);
  if (companyResult !== null) return companyResult;

  const dealResult = renderDealCell(value, id, isEditing);
  if (dealResult !== null) return dealResult;

  // Phone / email → Hub compose / SMS modals (ContactMethodActions stops propagation on cards)
  const contactMethodResult = renderContactMethodCell(value, fieldConfig, id, entityContext);
  if (contactMethodResult !== null) return contactMethodResult;

  // Scalar / type-based rendering
  const scalarResult = renderScalarCell(value, fieldConfig, id, isEditing);
  if (scalarResult !== undefined) return scalarResult;

  return String(value);
};
